import React from "react";
import { useTranslation } from "react-i18next";
import logoPilari from "../assets/images/pilari_light.png";
import logoGZW from "../assets/images/gzw.png";
import {
  UserCheck,
  Rocket,
  Image,
  FlaskConical,
  MessageCircle,
  FileText,
  Share2,
  Gift,
  NotebookPen,
  Handshake,
  PartyPopper,
  MapPin,
  CalendarClock,
  ClipboardList,
} from "lucide-react";

const InvitationPage = () => {
  const { t } = useTranslation();
  const rsvpLink = "https://forms.gle/ZEH37nW8U6oN4Doq9";

  const SectionDivider = ({ icon: Icon }) => (
    <div className="flex items-center my-6">
      <div className="flex-grow h-px bg-gradient-to-r from-transparent via-gray-300 to-transparent" />
      {Icon && <Icon className="mx-3 w-4 h-4 text-gray-400" />}
      <div className="flex-grow h-px bg-gradient-to-r from-transparent via-gray-300 to-transparent" />
    </div>
  );

  const rundownItems = Array.from({ length: 11 }, (_, i) => {
    const n = i + 1;
    const icons = [
      ClipboardList, UserCheck, MessageCircle, Rocket, Image, FlaskConical,
      MessageCircle, Gift, NotebookPen, Handshake, PartyPopper
    ];
    return {
      time: [
        "09.00", "09.30", "09.40", "09.55", "10.00", "10.10",
        "10.25", "10.45", "11.05", "11.25", "11.30"
      ][i],
      icon: icons[i],
      title: t(`invitation.rundown.${n}.title`),
      desc: t(`invitation.rundown.${n}.desc`)
    };
  });

  return (
    <div className="min-h-screen bg-gray-100 dark:bg-gray-900 py-24 px-4 text-gray-800 dark:text-gray-200 transition-colors duration-300">
      <div className="max-w-3xl mx-auto bg-white dark:bg-gray-800 rounded-3xl shadow-xl overflow-hidden">
        {/* Hero */}
        <div className="relative h-[320px] sm:h-[400px]">
          <img
            src={require("../assets/images/cikuray.webp")}
            alt="Gunung Cikuray"
            className="absolute inset-0 w-full h-full object-cover brightness-75"
          />
          <div className="absolute inset-0 flex flex-col items-center justify-center text-white text-center px-6">
            <img
              src={logoPilari}
              alt="Logo PILARI"
              className="w-20 sm:w-24 mb-3 drop-shadow-md"
            />
            <h1 className="text-3xl sm:text-4xl font-bold drop-shadow-lg">
              {t("invitation.title")}
            </h1>
            <p className="text-sm sm:text-base font-medium drop-shadow-sm mt-2">
              {t("invitation.subtitle")}
            </p>
          </div>
        </div>

        {/* Konten utama */}
        <div className="p-8 space-y-10 text-sm sm:text-base">
          {/* Info Acara */}
          <section className="text-center space-y-1 text-gray-700 dark:text-gray-300">
            <p className="font-medium">📆 {t("invitation.info.date")}</p>
            <p>⏰ {t("invitation.info.time")}</p>
            <p>📌 {t("invitation.info.place")}</p>
          </section>

          <SectionDivider icon={ClipboardList} />

          {/* Rundown Acara */}
          <section className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-100 dark:border-gray-700 shadow px-6 py-7">
            <h2 className="text-lg font-semibold text-green-700 flex justify-center items-center gap-2 mb-6">
              <CalendarClock className="w-5 h-5" />
              {t("invitation.rundown.title")}
            </h2>
            <div className="timeline-wrapper relative">
              <ul className="grid grid-cols-[5rem_2rem_1fr] gap-y-8">
                {rundownItems.map((item, idx) => (
                  <React.Fragment key={idx}>
                    <div className="text-right text-sm text-gray-500 dark:text-gray-400 pt-1 pr-2">
                      {item.time}
                    </div>
                    <div className="relative flex justify-center">
                      <span className="w-3 h-3 rounded-full border-2 border-green-600 bg-white dark:bg-gray-800 z-10 mt-1"></span>
                    </div>
                    <div className="flex items-start gap-2">
                      <item.icon className="w-4 h-4 text-gray-500 dark:text-gray-300 mt-1" />
                      <div>
                        <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                          {item.title}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          {item.desc}
                        </p>
                      </div>
                    </div>
                  </React.Fragment>
                ))}
              </ul>
            </div>
          </section>

          <SectionDivider icon={MapPin} />

          {/* Lokasi */}
          <section className="text-center space-y-4">
            <div className="overflow-hidden rounded-2xl border border-gray-200 dark:border-gray-700 shadow-md">
              <iframe
                title="Lokasi"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d63322.5633239074!2d107.88418439179806!3d-7.212666662017338!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x2e68b1ebc4cb8d5d%3A0x8635015328ccbaf2!2sDIDIEU%20Library%20Learning%20Space%20%26%20Coffee!5e0!3m2!1sid!2sid!4v1716014374897!5m2!1sid!2sid"
                width="100%"
                height="200"
                style={{ border: 0 }}
                loading="lazy"
                allowFullScreen
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
            <a
              href="https://maps.app.goo.gl/8mY3TjQhYrTY9M9dA"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block text-sm text-green-600 font-medium hover:underline hover:text-green-700 transition"
            >
              {t("invitation.location.viewMap")}
            </a>
          </section>

          <SectionDivider icon={FileText} />

          {/* RSVP */}
          <section className="text-center space-y-4">
            <a
              href={rsvpLink}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-green-700 text-white text-sm px-5 py-2 rounded-md hover:bg-green-800 transition"
            >
              <FileText className="w-4 h-4" />
              {t("invitation.rsvp.form")}
            </a>
          </section>

          <SectionDivider icon={Share2} />

          {/* Share */}
          <section className="text-center">
            <a
              href={`https://wa.me/?text=${encodeURIComponent(
                `🎉 *${t("invitation.title")}* 🎉\n\n` +
                  `📅 *${t("invitation.info.date")}*\n🕒 *${t(
                    "invitation.info.time"
                  )}*\n📍 *${t("invitation.info.place")}*\n\n` +
                  `📋 *${t("invitation.rundown.title")}:*\n` +
                  rundownItems.map((r) => `${r.time} – ${r.title}`).join("\n") +
                  `\n\n🗺️ *${t("invitation.location.viewMap")}:*\nhttps://maps.app.goo.gl/8mY3TjQhYrTY9M9dA\n\n` +
                  `📩 *${t("invitation.rsvp.form")}:*\n${rsvpLink}`
              )}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-5 py-2 bg-green-600 text-white text-sm rounded-md hover:bg-green-700 transition"
            >
              <Share2 className="w-4 h-4" />
              {t("invitation.share")}
            </a>
          </section>
        </div>

        {/* Footer */}
        <footer className="bg-gray-50 dark:bg-gray-900 border-t border-gray-200 dark:border-gray-700 text-center py-4">
          <div className="inline-flex items-center justify-center gap-2 text-xs text-gray-500 dark:text-gray-400">
            <span>{t("invitation.footer")}</span>
            <img src={logoGZW} alt="Garut Zero Waste" className="h-8" />
          </div>
        </footer>
      </div>
    </div>
  );
};

export default InvitationPage;
